import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import { SelectInput } from "@/components/admin/select-input";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { useEsAdmin } from "../datos/useEsAdmin";

const mismoSector = (a: string, b: string) =>
  a.localeCompare(b, "es", { sensitivity: "base" }) === 0;

/**
 * Desplegable de sector con «+ Crear sector…» (v1.1, petición de Robert y Jan): pide el
 * nombre, lo añade a la lista de Ajustes (`configuration.config.nevora.sectores`) y lo
 * deja seleccionado. Solo los administradores escriben en Ajustes (RLS): para el resto
 * es el desplegable de siempre. Un sector que ya exista (sin distinguir mayúsculas ni
 * acentos) se selecciona en vez de duplicarse.
 */
export const SelectorSector = ({
  sectorActual,
}: {
  /** Sector guardado en la ficha, por si ya no está en la lista de Ajustes. */
  sectorActual?: string | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const esAdmin = useEsAdmin();
  const { ajustes } = useAjustes();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const queryClient = useQueryClient();
  // Sectores creados en esta pantalla: se muestran aunque Ajustes aún no se haya recargado.
  const [creados, setCreados] = useState<string[]>([]);

  const nombres = [...ajustes.sectores];
  for (const s of [...creados, sectorActual ?? ""]) {
    if (s && !nombres.some((n) => mismoSector(n, s))) nombres.push(s);
  }
  const choices = nombres.map((s) => ({ id: s, name: s }));

  // Devuelve el id (texto) del sector a seleccionar, o undefined si se cancela. El
  // SelectInput de shadcn-admin-kit pasa lo devuelto tal cual a field.onChange, así que
  // tiene que ser el valor, no el objeto {id, name} de react-admin.
  const crear = async (): Promise<string | undefined> => {
    const nombre = window
      .prompt(translate("nevora.negocios.nuevo_sector_pregunta"))
      ?.trim();
    if (!nombre) return undefined;
    const existente = nombres.find((n) => mismoSector(n, nombre));
    if (existente) return existente;
    try {
      // Se leen los ajustes frescos de la base: así no se pisa un cambio reciente de
      // Ajustes (umbrales, intentos) que la caché de useAjustes aún no tuviera.
      const actuales = await dataProvider.getAjustes();
      const repetido = actuales.sectores.find((n) => mismoSector(n, nombre));
      if (!repetido) {
        await dataProvider.updateAjustes({
          ...actuales,
          sectores: [...actuales.sectores, nombre],
        });
        await queryClient.invalidateQueries({ queryKey: ["configuration"] });
      }
      setCreados((c) => [...c, repetido ?? nombre]);
      notify("nevora.negocios.sector_creado", {
        type: "success",
        messageArgs: { sector: repetido ?? nombre },
      });
      return repetido ?? nombre;
    } catch {
      notify("nevora.ajustes.error_guardar", { type: "error" });
      return undefined;
    }
  };

  return (
    <SelectInput
      source="sector"
      choices={choices}
      helperText={false}
      emptyText="—"
      onCreate={esAdmin ? crear : undefined}
      createLabel={translate("nevora.negocios.nuevo_sector")}
    />
  );
};
